import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { FilterService } from '@progress/kendo-angular-grid';

import {
  CheckBoxFilterMenuBase,
  CheckBoxFilterMenuOptions,
} from './grid-checkbox-filter-menu.base';

/**
 * https://www.telerik.com/kendo-angular-ui/components/grid/filtering/reusable-filter/ + refactor and renaming was done
 *
 * OnPush since WI 35564. The decorator-input API is unchanged (same names and types), but
 * every input the template or a computed depends on is stored in a signal by its setter, so
 * an input change re-derives the list and a checkbox/search event repaints through signals.
 * Behavior lives in CheckBoxFilterMenuBase, shared with grid-checkbox-filter-menu-signal.
 */
@Component({
  selector: 'grid-checkbox-filter-menu',
  templateUrl: 'grid-checkbox-filter-menu.component.html',
  styleUrls: ['./grid-checkbox-filter-menu.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckBoxFilterMenuComponent
  extends CheckBoxFilterMenuBase
  implements OnInit
{
  private readonly s = {
    height: signal(200),
    isLoadingFilterData: signal(false),
    isPrimitive: signal<boolean>(undefined),
    textField: signal<string>(undefined),
    valueField: signal<string>(undefined),
    hintField: signal<string>(undefined),
    isSingleSelection: signal(false),
    alwaysShowSelectedItems: signal(true), // NOTE: should be true by def.
    showFilter: signal(true),
    showFilterOperators: signal(false),
    addSelectAllOption: signal(false),
    addBlankOption: signal(false),
    customBlankOptionText: signal<string>(undefined),
  };

  protected readonly rawItems = signal<unknown[] | undefined>(undefined);
  protected readonly searchOperator = signal('contains');
  private initialized = false;
  private _currentFilter: CompositeFilterDescriptor;

  @Input() public set height(v: number) {
    this.s.height.set(v);
  }
  public get height() {
    return this.s.height();
  }
  @Input() public set isLoadingFilterData(v: boolean) {
    this.s.isLoadingFilterData.set(v);
  }
  public get isLoadingFilterData() {
    return this.s.isLoadingFilterData();
  }

  @Input() public set isPrimitive(v: boolean) {
    this.s.isPrimitive.set(v);
  }
  public get isPrimitive() {
    return this.s.isPrimitive();
  }
  @Input() public set textField(v: string) {
    this.s.textField.set(v);
  }
  public get textField() {
    return this.s.textField();
  }
  @Input() public set valueField(v: string) {
    this.s.valueField.set(v);
  }
  public get valueField() {
    return this.s.valueField();
  }
  /** Optional item field shown as each option's hover title (US 35432 stage tips). */
  @Input() public set hintField(v: string) {
    this.s.hintField.set(v);
  }
  public get hintField() {
    return this.s.hintField();
  }

  /** Seeds the checked set once, on the first filter the grid assigns. */
  @Input() public set currentFilter(f: CompositeFilterDescriptor) {
    this._currentFilter = f;
    if (this.initialized) this.seedFromFilter(f);
  }
  public get currentFilter() {
    return this._currentFilter;
  }
  @Input() public filterService: FilterService;

  @Input() public field: string;
  @Input() public operator = 'eq'; // NOTE: originally it was contains, but it has to be eq for checkbox selection!

  @Input() public compositeLogic: 'or' | 'and' = 'or';

  @Input() public collectionField: string;
  @Input() public collectionRef: string;
  @Input() public collectionItemField: string;
  @Input() public collectionOperator: 'any' | 'all' = 'any';

  @Input() public set isSingleSelection(v: boolean) {
    this.s.isSingleSelection.set(v);
  }
  public get isSingleSelection() {
    return this.s.isSingleSelection();
  }
  @Input() public set alwaysShowSelectedItems(v: boolean) {
    this.s.alwaysShowSelectedItems.set(v);
  }
  public get alwaysShowSelectedItems() {
    return this.s.alwaysShowSelectedItems();
  }
  @Input() public keepExtraFilterValues = false;

  @Input() public set showFilter(v: boolean) {
    this.s.showFilter.set(v);
  }
  public get showFilter() {
    return this.s.showFilter();
  }
  @Input() public set showFilterOperators(v: boolean) {
    this.s.showFilterOperators.set(v);
  }
  public get showFilterOperators() {
    return this.s.showFilterOperators();
  }

  @Input() public set addSelectAllOption(v: boolean) {
    this.s.addSelectAllOption.set(v);
  }
  public get addSelectAllOption() {
    return this.s.addSelectAllOption();
  }

  @Input() public set addBlankOption(v: boolean) {
    this.s.addBlankOption.set(v);
  }
  public get addBlankOption() {
    return this.s.addBlankOption();
  }
  @Input() public set customBlankOptionText(v: string) {
    this.s.customBlankOptionText.set(v);
  }
  public get customBlankOptionText() {
    return this.s.customBlankOptionText();
  }

  @Input() public set dataItemSearchOperator(v: string) {
    this.searchOperator.set(v);
  }
  public get dataItemSearchOperator() {
    return this.searchOperator();
  }

  @Input() public isGuidValue = false;

  /** NOT USED ANYWHERE */
  @Output() public valueChange = new EventEmitter<number[]>();

  /** Items that are shown in the checkbox filter */
  @Input() public set data(items: unknown[]) {
    this.rawItems.set(items ?? []);
  }
  // the checkbox list (with the optional Blank option)
  public get data() {
    return this.menuData();
  }

  /** selected item's value fields */
  public get value(): unknown[] {
    return this.selected();
  }
  public set value(v: unknown[]) {
    this.selected.set(v ?? []);
  }

  /** items that are shown in the checkbox list after the search box filter */
  public get filteredDataItems(): unknown[] {
    return this.visibleItems();
  }

  public get dataItemFilterText(): string {
    return this.searchText();
  }
  public set dataItemFilterText(v: string) {
    this.searchText.set(v);
  }

  /**
   * Seeds the checked set from the assigned filter BEFORE the first render. The Default
   * original did this in ngAfterViewInit and relied on the next global tick to repaint the
   * checkboxes, which an OnPush view never gets (WI 35564 negative control).
   */
  public ngOnInit(): void {
    this.initialized = true;
    this.seedFromFilter(this._currentFilter);
  }

  protected opts(): CheckBoxFilterMenuOptions {
    const s = this.s;
    return {
      height: s.height(),
      isLoadingFilterData: s.isLoadingFilterData(),
      isPrimitive: s.isPrimitive(),
      textField: s.textField(),
      valueField: s.valueField(),
      hintField: s.hintField(),
      currentFilter: this._currentFilter,
      filterService: this.filterService,
      field: this.field,
      operator: this.operator,
      compositeLogic: this.compositeLogic,
      collectionField: this.collectionField,
      collectionRef: this.collectionRef,
      collectionItemField: this.collectionItemField,
      collectionOperator: this.collectionOperator,
      isSingleSelection: s.isSingleSelection(),
      alwaysShowSelectedItems: s.alwaysShowSelectedItems(),
      keepExtraFilterValues: this.keepExtraFilterValues,
      showFilter: s.showFilter(),
      showFilterOperators: s.showFilterOperators(),
      addSelectAllOption: s.addSelectAllOption(),
      addBlankOption: s.addBlankOption(),
      customBlankOptionText: s.customBlankOptionText(),
      isGuidValue: this.isGuidValue,
    };
  }
}
